<template>
  <div>
    <div class="task-row">
      <a :href="appEditorUrl" target="_blank">
        {{ value.appId }} : {{ value.url }}
      </a>

      <div>
        <b-spinner v-if="isLoading || isDeleting" />
        <Button
          v-if="removable"
          size="sm ml-2"
          @click="remove"
          tabindex="-1"
          variant="light"
        >
          <i class="bi bi-x" />
        </Button>
      </div>
    </div>

    <div class="task-row">
      <div>
        {{ value.status }}
        <small>{{ value.taskId }}</small>
      </div>

      <div class="task-times">
        <div>Started: {{ timeSinceCreated }}</div>
        <div>Checked: {{ timeSinceChecked }}</div>
      </div>
    </div>

    <b-row>
      <b-col>
        <prism-editor
          v-model="value.resultString"
          language="json"
          read-only="true"
          line-numbers
          class="editor"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapState } from 'vuex';
import { formatRelative } from 'date-fns';

import store from '@/plugins/vuex';
import { Task } from '@/vuex/tasks';
import Button from '@/components/Button.vue';

const REFRESH_TASK_THRESHOLD = 10 * 1000;

export default defineComponent({
  name: 'TaskItem',
  components: {
    Button,
  },
  props: {
    value: {
      type: Object as () => Task,
      required: true,
    },
  },
  data() {
    return {
      timer: null as any,
    };
  },
  mounted() {
    // TODO: Start first timer here
    this.loadTask();
  },
  destroyed() {
    if (this.timer) {
      clearTimeout(this.timer);
    }
  },
  computed: {
    ...mapState({
      // tasks: (state: any) => state.task.tasks,
      loadingTasks: (state: any) => state.task.loadingTasks,
      deletingTasks: (state: any) => state.task.deletingTasks,
    }),
    isLoading(): boolean {
      // return true;
      return this.loadingTasks.includes(this.value.taskId);
    },
    isDeleting(): boolean {
      return this.deletingTasks.includes(this.value.taskId);
    },
    timeSinceCreated(): string {
      return formatRelative(this.value.created, new Date(), {
        weekStartsOn: 1,
      });
    },
    timeSinceChecked(): string {
      return formatRelative(this.value.checked, new Date(), {
        weekStartsOn: 1,
      });
    },
    removable(): boolean {
      return (
        !this.isLoading && !this.isDeleting && this.value.status !== 'STARTED'
      );
    },
    appEditorUrl(): string {
      return `${this.value.url}/#/edit/${this.value.appId}`;
    },
  },
  methods: {
    remove(): void {
      store.dispatch.task.deleteTask(this.value.taskId);
    },
    async loadTask(): Promise<void> {
      if (this.removable || this.isLoading) {
        return;
      }

      let diff = Date.now() - this.value.checked.getTime();
      if (REFRESH_TASK_THRESHOLD < diff) {
        await store.dispatch.task.loadTask(this.value.taskId);
        diff = 0;
      }
      this.timer = setTimeout(
        () => this.loadTask(),
        REFRESH_TASK_THRESHOLD - diff
      );
    },
  },
});
</script>
<style scoped>
.task-row {
  display: flex;
  width: 100%;
  align-items: flex-start;
  justify-content: space-between;
}

.task-times {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}
</style>
